import { FC, useState, useEffect } from 'react';
import { Modal, Button, TextInput, Label, Spinner, Alert, ListGroup } from '../../';
import stewardReportServices from '../../../services/stewardReportServices';
import { MemberSearchResult } from '../../../services/dtos';

interface AddWorkerModalProps {
  show: boolean;
  onClose: () => void;
  onSelect: (member: MemberSearchResult) => void;
  jobId?: string; // Optional, no longer used but kept for interface compatibility
}

export const AddWorkerModal: FC<AddWorkerModalProps> = ({
  show,
  onClose,
  onSelect,
}) => {
  // Search state
  const [lastName, setLastName] = useState('');
  const [last4SSN, setLast4SSN] = useState('');
  const [searchResults, setSearchResults] = useState<MemberSearchResult[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const svc = new stewardReportServices();

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setLastName('');
      setLast4SSN('');
      setSearchResults([]);
      setSearchError(null);
      setSearchPerformed(false);
    }
  }, [show]);

  const handleSearch = async () => {
    if (!lastName.trim() && !last4SSN.trim()) {
      setSearchError('Please enter at least a last name or last 4 digits of SSN.');
      return;
    }

    if (last4SSN.trim() && (last4SSN.trim().length !== 4 || !/^\d+$/.test(last4SSN.trim()))) {
      setSearchError('Last 4 SSN must be exactly 4 digits.');
      return;
    }

    setLoadingSearch(true);
    setSearchError(null);
    setSearchPerformed(true);

    try {
      const results = await svc.searchMembers(
        lastName.trim() || undefined,
        last4SSN.trim() || undefined
      );
      setSearchResults(results);

      if (results.length === 0) {
        setSearchError('No members found matching your search criteria.');
      }
    } catch (err: any) {
      console.error('Error searching members:', err);
      setSearchError('Failed to search members. Please try again.');
      setSearchResults([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSelectMember = (member: MemberSearchResult) => {
    onSelect(member);
    onClose();
  };

  const canSearch = lastName.trim().length > 0 || (last4SSN.trim().length === 4 && /^\d+$/.test(last4SSN.trim()));

  return (
    <Modal show={show} onClose={onClose} size="lg">
      <Modal.Header>Add Worker</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Enter at least one search criteria to find a member.
            </p>
          </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lastName" value="Last Name" />
                <TextInput
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSearch) {
                      handleSearch();
                    }
                  }}
                  placeholder="Partial match"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="last4SSN" value="Last 4 SSN" />
                <TextInput
                  id="last4SSN"
                  value={last4SSN}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setLast4SSN(value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSearch) {
                      handleSearch();
                    }
                  }}
                  placeholder="4 digits"
                  maxLength={4}
                  className="mt-1"
                />
              </div>
            </div>

            {searchError && (
              <Alert color="failure">{searchError}</Alert>
            )}

            {loadingSearch && (
              <div className="flex justify-center py-4">
                <Spinner size="lg" />
              </div>
            )}

            {!loadingSearch && searchPerformed && searchResults.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2 dark:text-white">
                  Found {searchResults.length} member{searchResults.length !== 1 ? 's' : ''}:
                </p>
                <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <ListGroup>
                      {searchResults.map((member) => (
                        <ListGroup.Item
                          key={member.uniqueMemberId}
                          onClick={() => handleSelectMember(member)}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex justify-between items-center py-2">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {member.fullName}
                            </div>
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                </div>
              </div>
            )}

          {!loadingSearch && searchPerformed && searchResults.length === 0 && !searchError && (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No members found. Try different search criteria.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSearch} disabled={!canSearch || loadingSearch}>
          {loadingSearch ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Searching...
            </>
          ) : (
            'Search'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

